import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { fetchAddressAreaByPin, updateAddressArea } from "../../service/AddressApi";
import "../general/General.css";
import { useParams } from "react-router-dom";
import { useCheckAccess } from "../../utils/Utilities";
import { Loading, Navbar, Footer, Breadcrumbs } from "../../components/shared";
import { Helmet } from "react-helmet-async";


const AddressAreaUpdate = () => {
  useCheckAccess('AddressRW'); // Check if the user is logged in
  const [addressAreaData, setAddressAreaData] = useState({ addressAreaList: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedArea, setSelectedArea] = useState(null);
  const { pincode } = useParams();
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm();

  // Fetch address area data by pin
  useEffect(() => {
    const fetchData = async () => {
      if (pincode) {
        try {
          setLoading(true);
          const data = await fetchAddressAreaByPin(pincode, 'false'); // Fetch the data
          setAddressAreaData(data || { addressAreaList: [] }); // Handle response data or set an empty array
        } catch (err) {
          setError("Failed to fetch address data.");
          console.error(err); // Log error for debugging
        } finally {
          setLoading(false);
        }
      } else {
        setError("Invalid PIN received");
        setLoading(false);
      }
    };

    fetchData();
  }, [pincode]);

  const handleFormSubmit = async () => {
    if (!selectedArea?.id) {
      setError("Please select an area before submitting.");
      return; // Prevent submission if no area is selected
    }
    setLoading(true);
    const payload = {
      id: selectedArea.id,  // Ensure selectedArea.id is present
      name: watch("name"),  // Use the English name from the form
      name_tamil: watch("name_tamil"),  // Use the Tamil name from the form
      block: watch("block"),
      block_tamil: watch("block_tamil"),
      district: watch("district"),
      district_tamil: watch("district_tamil"),
    };
    try {
      const success = await updateAddressArea(payload);
      if (success) {
        navigate(`/address-area/view/${pincode}`);
      }
    } catch (error) {
      console.error("Error updating address area:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle area selection
  const handleAreaSelect = (e) => {
    const selectedId = e.target.value;
    const selected = addressAreaData.addressAreaList.find(area => area.id === selectedId);

    if (selected) {
      setSelectedArea(selected);  // Update the selected area state
      setValue("name", selected.name); // Set the existing name in the form
      setValue("name_tamil", selected.name_tamil); // Set the existing Tamil name in the form
      setValue("block", selected.block); // Set the existing block in the form
      setValue("block_tamil", selected.block_tamil); // Set the existing Tamil block in the form
      setValue("district", selected.district); // Set the existing district in the form
      setValue("district_tamil", selected.district_tamil); // Set the existing Tamil district in the form
      setValue("state", selected.state); // Set the existing state in the form
      setValue("state_tamil", selected.state_tamil); // Set the existing Tamil state in the form
    } else {
      setSelectedArea(null);  // Clear selected area if none is found
    }
    setError(""); // Clear error on selection change
  };

  // Display loading or error states
  if (loading) {
    return (
      <>
        <Navbar />
        <div className="general_parent">
          <div className="general_container">
            <div className="general_body">
              <Breadcrumbs /><br />
              <Loading />
            </div>
          </div>
        </div>
        <Footer />
      </>

    );
  }

  if (error) {
    return (
      <Loading />
    );
  }



  return (
    <>
      <Navbar />
      <Helmet>
        <title>முகவரியை புதுப்பிக்கவும்</title>
        <link rel="canonical" href="/address-area/update" />
      </Helmet>

      <div className="general_parent">
        <div className="general_body">
          <Breadcrumbs /><br />
          <h3>பின்னுக்கான அஞ்சல்கள்: {pincode}</h3>
        </div>
      </div>

      <div className="general_parent">
        <div className="general_container">
          <div className="general_body">
            <button onClick={() => navigate("/address/admin")}>முகவரி நிர்வாகி பக்கம்</button>
            <br />
            <button onClick={() => navigate("/address-area/create")}>அஞ்சலை உருவாக்கு</button>
          </div>
        </div>
      </div>

      {addressAreaData.addressAreaList && addressAreaData.addressAreaList.length > 0 ? (
        <div className="general_parent">
          <div className="general_container">
            <div className="general_body">
              {/* Dropdown to select Area */}
              <h3>அஞ்சலைத் தேர்ந்தெடுக்கவும்</h3>
              <select onChange={handleAreaSelect} value={selectedArea?.id || ""}
                className="auth_select"
              >
                <option value="">அஞ்சலைத் தேர்ந்தெடுக்கவும்</option>
                {addressAreaData.addressAreaList.map((addressArea) => (
                  <option key={addressArea.id} value={addressArea.id}>
                    {addressArea.name},{addressArea.block},{addressArea.district}, {addressArea.state} ({addressArea.name_tamil},{addressArea.block_tamil}, {addressArea.district_tamil})
                  </option>
                ))}
              </select>

              {/* Form to update Area data */}
              {selectedArea && (
                <>
                  <br />
                  <h3>அஞ்சல் விவரங்களைப் புதுப்பிக்கவும்.</h3>

                  <form onSubmit={handleSubmit(handleFormSubmit)} className="auth_form" autoComplete="off">
                    <div className="auth_element">
                      <label className="auth_label">Post Office (English)</label>
                      <input
                        type="text"
                        {...register("name", { required: "Post Office (English) is required" })}
                        className="auth_input"
                        placeholder="Enter Post Office (English)"
                      />
                      {errors.name && <div className="auth_error">{errors.name.message}</div>}
                    </div>
                    <div className="auth_element">
                      <label className="auth_label">Post Office (Tamil)</label>
                      <input
                        type="text"
                        {...register("name_tamil", { required: "Post Office (Tamil) is required" })}
                        className="auth_input"
                        placeholder="Enter Post Office (Tamil)"
                      />
                      {errors.name_tamil && <div className="auth_error">{errors.name_tamil.message}</div>}
                    </div>

                    <div className="auth_element">
                      <label className="auth_label">Taluk (English)</label>
                      <input
                        type="text"
                        {...register("block", { required: "Taluk is required" })}
                        className="auth_input"
                        placeholder="Enter Taluk (English)"
                      />
                      {errors.block && <div className="auth_error">{errors.block.message}</div>}
                    </div>
                    <div className="auth_element">
                      <label className="auth_label">Taluk (Tamil)</label>
                      <input
                        type="text"
                        {...register("block_tamil", { required: "Taluk (Tamil) is required" })}
                        className="auth_input"
                        placeholder="Enter Taluk (Tamil)"
                      />
                      {errors.block_tamil && <div className="auth_error">{errors.block_tamil.message}</div>}
                    </div>

                    <div className="auth_element">
                      <label className="auth_label">District (English)</label>
                      <input
                        type="text"
                        {...register("district", { required: "District is required" })}
                        className="auth_input"
                        placeholder="Enter District (English)"
                      />
                      {errors.district && <div className="auth_error">{errors.district.message}</div>}
                    </div>
                    <div className="auth_element">
                      <label className="auth_label">District (Tamil)</label>
                      <input
                        type="text"
                        {...register("district_tamil", { required: "District (Tamil) is required" })}
                        className="auth_input"
                        placeholder="Enter District (Tamil)"
                      />
                      {errors.district_tamil && <div className="auth_error">{errors.district_tamil.message}</div>}
                    </div>


                    <button type="submit" className="auth_submit" disabled={loading}>
                      அஞ்சல் விவரங்களைப் புதுப்பிக்கவும்.
                    </button>
                  </form>

                  {/* Display existing Sub-Areas */}
                  <hr />

                </>
              )}
            </div>
          </div>
        </div>
      ) : ("No adderss details found")}

      <br />
      <Footer />
    </>
  );
};

export default AddressAreaUpdate;
